import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {Disclosure} from '@headlessui/react';
import {AlertTriangle, ChevronDown, ChevronRight, Pencil, Plus, RefreshCw, Save, Trash2, XCircle} from 'lucide-react';
import clsx from 'clsx';
import {toast} from 'react-toastify';
import {useTenant} from '@/shared/hooks/useTenant';
import Card from '@/shared/components/Card';
import {CatalogTestDotRow, CatalogBadge} from '@/shared/components/catalog';
import type {CatalogTestDisplay} from '@/shared/components/catalog';
import AdminService from '@/modules/admin/services/adminService';
import {
    usePricingCalculator,
    computeTestPrice,
    getAddonPrice,
    TIER_DEFAULT_MARKUP,
    type Multipliers,
} from '@/modules/provider-console/hooks/usePricingCalculator';
import type {
    OrganizationCatalogPanel,
    OrganizationCatalogProfile,
    AssayCatalogItem,
    PartnerTier,
    PricingPanel,
    PricingSettings,
} from '@/shared/types/api';

function fmt(val: number): string {
    return val.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function formatProfileSummary(
    panels: Array<{id: number}>,
    wrapperPanelIdSet: Set<number>
): string {
    const regularCount = panels.filter((p) => !wrapperPanelIdSet.has(p.id)).length;
    const testCount = panels.filter((p) => wrapperPanelIdSet.has(p.id)).length;
    const parts: string[] = [];
    if (regularCount > 0) parts.push(`${regularCount} panel${regularCount === 1 ? '' : 's'}`);
    if (testCount > 0) parts.push(`${testCount} test${testCount === 1 ? '' : 's'}`);
    return parts.join(' · ') || 'Empty';
}

const TIER_LABELS: Record<PartnerTier, string> = {
    payg: 'PAYG (Pay As You Go)',
    standard: 'Standard Wallet',
    preferred: 'Preferred Wallet',
    enterprise: 'Enterprise',
};

/* ------------------------------------------------------------------ */
/*  Pricing Controls Card (shown above Profiles when editing)          */
/* ------------------------------------------------------------------ */
const PricingControlsCard: React.FC<{
    selectedTier: PartnerTier;
    onTierChange: (tier: PartnerTier) => void;
    multipliers: Multipliers;
    onMultiplierChange: (field: keyof Multipliers, value: string) => void;
    activeMarkup: number;
    markupOverride: number | null;
    onMarkupChange: (value: string) => void;
    onMarkupReset: () => void;
    memberDiscountPct: number | null;
    onMemberDiscountChange: (value: string) => void;
    onRefresh?: () => void;
}> = ({
    selectedTier,
    onTierChange,
    multipliers,
    onMultiplierChange,
    activeMarkup,
    markupOverride,
    onMarkupChange,
    onMarkupReset,
    memberDiscountPct,
    onMemberDiscountChange,
    onRefresh,
}) => (
    <Card>
        <Card.Content>
            <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-mld-text-secondary">Partner Tier:</label>
                    <div className="relative">
                        <select
                            value={selectedTier}
                            onChange={(e) => onTierChange(e.target.value as PartnerTier)}
                            className="appearance-none bg-none rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-sm font-medium text-mld-text-primary focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary">
                            <option value="payg">{TIER_LABELS.payg}</option>
                            <option value="standard">{TIER_LABELS.standard}</option>
                            <option value="preferred">{TIER_LABELS.preferred}</option>
                            <option value="enterprise">{TIER_LABELS.enterprise}</option>
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-mld-muted-text" />
                    </div>
                </div>

                {selectedTier === 'enterprise' && (
                    <>
                        <div className="h-6 w-px bg-mld-panel-border" />

                        <span className="text-sm font-medium text-mld-text-secondary">Multipliers</span>
                        {(['reagent', 'calibrator', 'shared'] as const).map((field) => (
                            <div key={field} className="flex items-center gap-1">
                                <label className="text-xs capitalize text-mld-muted-text">{field}:</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={multipliers[field]}
                                    onChange={(e) => onMultiplierChange(field, e.target.value)}
                                    className="w-14 rounded border border-mld-panel-border px-1.5 py-1 text-center text-sm focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                />
                            </div>
                        ))}

                        <div className="h-6 w-px bg-mld-panel-border" />

                        <span className="text-sm font-medium text-mld-text-secondary">Markup</span>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                step="0.1"
                                min="2"
                                value={activeMarkup}
                                onChange={(e) => onMarkupChange(e.target.value)}
                                className="w-14 rounded border border-mld-panel-border px-1.5 py-1 text-center text-sm focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                            />
                            <span className="text-xs text-mld-muted-text">&times;</span>
                        </div>
                        {markupOverride !== null && (
                            <button
                                onClick={onMarkupReset}
                                className="rounded px-2 py-0.5 text-xs font-medium text-mld-muted-text hover:bg-mld-menu-background">
                                Reset
                            </button>
                        )}
                    </>
                )}

                <div className="h-6 w-px bg-mld-panel-border" />

                <span className="text-sm font-medium text-mld-text-secondary">My Labs+ Discount</span>
                <div className="flex items-center gap-1">
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={memberDiscountPct ?? ''}
                        onChange={(e) => onMemberDiscountChange(e.target.value)}
                        className="w-16 rounded border border-mld-panel-border px-1.5 py-1 text-center text-sm focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                    />
                    <span className="text-xs text-mld-muted-text">%</span>
                </div>

                {onRefresh && (
                    <button
                        type="button"
                        onClick={onRefresh}
                        className="ml-auto rounded p-1.5 text-mld-muted-text hover:bg-mld-menu-background hover:text-mld-text-secondary"
                        title="Refresh catalog data">
                        <RefreshCw className="h-4 w-4" />
                    </button>
                )}
            </div>
        </Card.Content>
    </Card>
);

/* ------------------------------------------------------------------ */
/*  Panel Edit Mode — inline expansion                                 */
/* ------------------------------------------------------------------ */
const PanelEditMode: React.FC<{
    assays: AssayCatalogItem[];
    orphanTests?: Array<{code: string; name: string}>;
    editingCodes: Set<string>;
    onToggleCode: (code: string) => void;
    onSelectAll: () => void;
    onClearAll: () => void;
    multipliers: Multipliers;
    activeMarkup: number;
    selectedTier: PartnerTier;
    panelName: string;
    onPanelNameChange: (name: string) => void;
    onSave: () => void;
    onSaveAsNew: () => void;
    onCancel: () => void;
    isSaving: boolean;
    isExistingPanel: boolean;
    summary: ReturnType<typeof usePricingCalculator>;
}> = ({
    assays,
    orphanTests = [],
    editingCodes,
    onToggleCode,
    onSelectAll,
    onClearAll,
    multipliers,
    activeMarkup,
    selectedTier,
    panelName,
    onPanelNameChange,
    onSave,
    onSaveAsNew,
    onCancel,
    isSaving,
    isExistingPanel,
    summary,
}) => {
    const individualAssays = useMemo(() => {
        const filtered = assays.filter((a) => !a.is_bundled_panel && a.classification != null);
        // Sort checked items to the top so they're easy to find
        return [...filtered].sort((a, b) => {
            const aChecked = editingCodes.has(a.code) ? 0 : 1;
            const bChecked = editingCodes.has(b.code) ? 0 : 1;
            if (aChecked !== bChecked) return aChecked - bChecked;
            return a.name.localeCompare(b.name);
        });
    }, [assays, editingCodes]);
    const canSave = panelName.trim().length > 0 && editingCodes.size > 0 && !isSaving;
    const isEnterprise = selectedTier === 'enterprise';
    const colCount = isEnterprise ? 5 : 3;

    const renderRow = (assay: AssayCatalogItem) => {
        const included = editingCodes.has(assay.code);
        const unmapped = assay.is_calculated && !assay.elis_test_code;
        const {cost, price} = computeTestPrice(assay, multipliers, activeMarkup);
        const addon = getAddonPrice(assay, selectedTier);
        return (
            <tr
                key={assay.code}
                className={clsx(
                    'border-b border-mld-warning/15 transition-colors',
                    unmapped ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
                    included ? 'bg-mld-primary/5/40' : !unmapped && 'hover:bg-mld-warning/5/50'
                )}
                onClick={() => !unmapped && onToggleCode(assay.code)}>
                <td className="px-4 py-1.5">
                    <input
                        type="checkbox"
                        checked={included}
                        disabled={unmapped}
                        onChange={() => !unmapped && onToggleCode(assay.code)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-3.5 w-3.5 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary disabled:cursor-not-allowed disabled:opacity-50"
                    />
                </td>
                <td className="px-2 py-1.5 text-xs text-mld-text-primary">
                    {assay.name}
                    {unmapped && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-mld-warning" title="Not yet mapped to an ELIS target code">
                            <AlertTriangle size={10} /> pending ELIS
                        </span>
                    )}
                </td>
                {assay.classification === 4 ? (
                    <td colSpan={isEnterprise ? 2 : 1} className="px-2 py-1.5 text-right text-[11px] font-semibold text-mld-warning">
                        TBD
                    </td>
                ) : assay.is_calculated ? (
                    <td colSpan={isEnterprise ? 2 : 1} className="px-2 py-1.5 text-right text-[11px] font-semibold text-mld-success">
                        Free
                    </td>
                ) : isEnterprise ? (
                    <>
                        <td className="px-2 py-1.5 text-right font-mono text-[11px] text-mld-muted-text">
                            ${fmt(cost)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-[11px]">
                            <span className={included ? 'font-medium text-mld-primary' : 'text-mld-muted-text'}>
                                ${fmt(price)}
                            </span>
                        </td>
                    </>
                ) : (
                    <td className="px-2 py-1.5 text-right font-mono text-[11px]">
                        <span className={included ? 'font-medium text-mld-primary' : 'text-mld-muted-text'}>
                            ${fmt(addon)}
                        </span>
                    </td>
                )}
            </tr>
        );
    };

    return (
        <div className="rounded-b-md border-l-4 border-l-amber-400 border-b border-r border-b-amber-200 border-r-amber-200 bg-mld-warning/5/30">
            {/* Panel name + Select All / Clear */}
            <div className="border-b border-mld-warning/15 px-4 py-3">
                <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-mld-text-secondary">Panel Name:</label>
                    <input
                        type="text"
                        value={panelName}
                        onChange={(e) => onPanelNameChange(e.target.value)}
                        placeholder="e.g. Women's Health Panel"
                        className="flex-1 rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-sm text-mld-text-primary placeholder-gray-400 focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                    />
                    <button
                        onClick={onSelectAll}
                        className="shrink-0 rounded px-2 py-0.5 text-xs font-medium text-mld-primary hover:bg-mld-primary/5">
                        Select All
                    </button>
                    <button
                        onClick={onClearAll}
                        className="shrink-0 rounded px-2 py-0.5 text-xs font-medium text-mld-muted-text hover:bg-mld-menu-background">
                        Clear
                    </button>
                </div>
            </div>

            {/* Test list */}
            <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-mld-warning/5">
                        <tr className="border-b border-mld-warning/30 text-left text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">
                            <th className="w-8 px-4 py-1.5" />
                            <th className="px-2 py-1.5">Test</th>
                            {isEnterprise ? (
                                <>
                                    <th className="px-2 py-1.5 text-right">Cost</th>
                                    <th className="px-2 py-1.5 text-right">Panel Price</th>
                                </>
                            ) : (
                                <th className="px-2 py-1.5 text-right">Add-On Price</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {individualAssays.map(renderRow)}
                        {orphanTests.map((t) => {
                            const included = editingCodes.has(t.code);
                            return (
                                <tr
                                    key={`orphan-${t.code}`}
                                    className={clsx(
                                        'cursor-pointer border-b border-mld-warning/15 transition-colors',
                                        included ? 'bg-mld-danger/5/60' : 'hover:bg-mld-warning/5/50'
                                    )}
                                    onClick={() => onToggleCode(t.code)}>
                                    <td className="px-4 py-1.5">
                                        <input
                                            type="checkbox"
                                            checked={included}
                                            onChange={() => onToggleCode(t.code)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="h-3.5 w-3.5 rounded border-mld-panel-border text-mld-danger focus:ring-rose-400"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5 text-xs text-mld-text-primary">
                                        {t.name}{' '}
                                        <span className="text-[10px] text-mld-danger">(not in catalog)</span>
                                    </td>
                                    <td className={`px-2 py-1.5 text-right text-[11px] text-mld-muted-text`}
                                        colSpan={isEnterprise ? 2 : 1}>
                                        &mdash;
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Totals + Save */}
            <div className="flex flex-wrap items-center justify-between border-t border-mld-warning/30 px-4 py-3">
                <div className="flex items-center gap-6 text-sm">
                    {isEnterprise ? (
                        <div>
                            <span className="text-mld-muted-text">Panel Total: </span>
                            <span className="font-semibold text-mld-primary">${fmt(summary.panelPrice)}</span>
                        </div>
                    ) : (
                        <div>
                            <span className="text-mld-muted-text">Add-On Total: </span>
                            <span className="font-semibold text-mld-primary">${fmt(summary.addonTotal)}</span>
                        </div>
                    )}
                    <div className="text-xs text-mld-muted-text">
                        {summary.testsIncluded} test{summary.testsIncluded === 1 ? '' : 's'} selected
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={onCancel}
                        className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                        Cancel
                    </button>
                    {isExistingPanel ? (
                        <>
                            <button
                                onClick={onSave}
                                disabled={!canSave}
                                className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                                <Save className="h-3.5 w-3.5" />
                                {isSaving ? 'Saving...' : 'Update Panel'}
                            </button>
                            <button
                                onClick={onSaveAsNew}
                                disabled={!canSave}
                                className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background disabled:cursor-not-allowed disabled:opacity-50">
                                Save as New
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onSaveAsNew}
                            disabled={!canSave}
                            className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                            <Save className="h-3.5 w-3.5" />
                            {isSaving ? 'Saving...' : 'Save as New Panel'}
                        </button>
                    )}
                </div>
            </div>

            {editingCodes.size === 0 && (
                <p className="px-4 pb-3 text-xs text-mld-muted-text">Select at least one test to save.</p>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Tests list inside an expanded panel (non-edit)                     */
/* ------------------------------------------------------------------ */
const IncludedTests: React.FC<{
    tests: OrganizationCatalogPanel['tests'];
    isBundled?: boolean;
    testPrices?: Map<number, number>;
    calculatedTestIds?: Set<number>;
}> = ({tests, isBundled, testPrices, calculatedTestIds}) => {
    if (!tests.length) {
        return <p className="py-2 text-xs text-mld-muted-text">No tests linked.</p>;
    }

    // Map admin OrganizationCatalogTest → shared CatalogTestDisplay
    const hasPricing = !isBundled && testPrices != null;
    const displayTests: CatalogTestDisplay[] = [...tests]
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map((t) => {
            const price = hasPricing ? (testPrices.get(t.id) ?? 0) : 0;
            const isCalc = !!calculatedTestIds?.has(t.id);
            return {
                id: t.id,
                name: t.name || 'Unnamed',
                code: t.code || '',
                addonPrice: price,
                isCalculated: isCalc,
                inAssayCatalog: t.in_assay_catalog,
                isMissingPrerequisite: !!t.is_missing_prerequisite,
            };
        });

    return (
        <div className="rounded border border-t-0 border-dashed border-mld-panel-border bg-white px-4 py-3">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">Included Tests</p>
            <div className="space-y-0.5">
                {displayTests.map((t) => (
                    <CatalogTestDotRow
                        key={t.isMissingPrerequisite ? `prereq-${t.code}` : t.id}
                        test={t}
                        includedInBundledPanel={!!isBundled}
                        showAlertIcon={hasPricing}
                    />
                ))}
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Panel accordion row                                                */
/* ------------------------------------------------------------------ */
const PanelRow: React.FC<{
    panel: OrganizationCatalogPanel;
    badge?: string;
    price?: number | null;
    isAdmin: boolean;
    isEditing: boolean;
    isBundled?: boolean;
    testPrices?: Map<number, number>;
    calculatedTestIds?: Set<number>;
    nameColWidth?: number;
    onEdit: () => void;
    onCancel: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
    editModeContent?: React.ReactNode;
}> = ({panel, badge, price, isAdmin, isEditing, isBundled, testPrices, calculatedTestIds, nameColWidth, onEdit, onCancel, onDelete, isDeleting, editModeContent}) => {
    const nameStyle = nameColWidth ? {minWidth: nameColWidth, width: nameColWidth} : undefined;
    if (isEditing) {
        return (
            <div>
                {/* Collapsed header in edit mode */}
                <div
                    className={clsx(
                        'flex w-full items-center gap-3 rounded-t-md px-3 py-2',
                        'border-l-4 border-l-amber-400 border-t border-r border-t-amber-200 border-r-amber-200 bg-mld-warning/5'
                    )}>
                    <span className="shrink-0 truncate text-xs font-medium text-mld-text-primary" style={nameStyle}>
                        {panel.name || 'Unnamed Panel'}
                    </span>
                    <span className="w-14 shrink-0 text-[10px] text-mld-muted-text">
                        {panel.tests.length} test{panel.tests.length === 1 ? '' : 's'}
                    </span>
                    {badge ? (
                        <CatalogBadge label={badge} variant="admin" />
                    ) : null}
                    {price != null && (
                        <span className="ml-auto font-mono text-xs font-medium text-mld-primary">
                            ${fmt(price)}
                        </span>
                    )}
                    <button
                        onClick={onCancel}
                        className="ml-2 inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-mld-muted-text hover:bg-mld-menu-background"
                        title="Cancel editing">
                        <XCircle className="h-3 w-3" />
                        Close
                    </button>
                </div>
                {editModeContent}
            </div>
        );
    }

    const isEmpty = panel.tests.length === 0;

    // Disabled row for panels with no matched tests
    if (isEmpty) {
        return (
            <div
                className="flex w-full items-center gap-3 rounded-md border border-mld-panel-border bg-mld-menu-background px-3 py-2 opacity-50">
                <span className="shrink-0 truncate text-xs font-medium text-mld-muted-text" style={nameStyle}>
                    {panel.name || 'Unnamed Panel'}
                </span>
                <span className="w-14 shrink-0 text-[10px] text-mld-muted-text">0 tests</span>
                {badge ? (
                    <span className="inline-flex shrink-0 items-center rounded-full bg-mld-menu-background px-2 py-0.5 text-[9px] font-semibold text-mld-muted-text ring-1 ring-gray-200">
                        {badge}
                    </span>
                ) : null}
                {isAdmin && (
                    <>
                        <span className="ml-auto font-mono text-xs font-medium text-mld-muted-text">—</span>
                        {!isBundled ? (
                            <div className="ml-1 flex items-center gap-0.5">
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onEdit()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') onEdit();
                                    }}
                                    className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-mld-muted-text opacity-100 hover:bg-mld-warning/5 hover:text-mld-warning"
                                    title="Edit panel pricing">
                                    <Pencil className="h-3 w-3" />
                                    <span>Edit</span>
                                </span>
                                {onDelete && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onDelete()}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') onDelete();
                                        }}
                                        className={clsx(
                                            'inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-medium',
                                            isDeleting ? 'text-mld-muted-text' : 'text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger'
                                        )}
                                        title="Delete panel">
                                        <Trash2 className="h-3 w-3" />
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span
                                aria-hidden="true"
                                className="invisible ml-1 inline-flex items-center gap-1 px-2 py-1 text-[10px]">
                                <Pencil className="h-3 w-3" />
                                <span>Edit</span>
                            </span>
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <Disclosure as="div">
            {({open}) => (
                <>
                    <Disclosure.Button
                        className={clsx(
                            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors',
                            open
                                ? 'border-y border-r border-y-mld-primary/20 border-r-mld-primary/20 border-l-[3px] border-l-mld-primary bg-mld-primary/5'
                                : 'border border-mld-panel-border bg-mld-background'
                        )}>
                        <span className="flex shrink-0 items-center gap-1.5 truncate text-xs font-medium text-mld-text-primary" style={nameStyle}>
                            {panel.name || 'Unnamed Panel'}
                            {panel.tests.some((t) => !t.in_assay_catalog || t.is_missing_prerequisite) && (
                                <span title={panel.tests.some((t) => t.is_missing_prerequisite) ? "Panel is missing prerequisite tests" : "Some tests do not match the assay catalog"}><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-mld-danger" /></span>
                            )}
                        </span>
                        <span className="w-14 shrink-0 text-[10px] text-mld-muted-text">
                            {panel.tests.length} test{panel.tests.length === 1 ? '' : 's'}
                        </span>
                        {badge ? (
                            <CatalogBadge label={badge} variant="admin" />
                        ) : null}
                        {isAdmin && price != null && (
                            <span className="ml-auto font-mono text-xs font-medium text-mld-primary">
                                ${fmt(price)}
                            </span>
                        )}
                        {isAdmin && !isBundled ? (
                            <div className="ml-1 flex items-center gap-0.5">
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        onEdit();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            onEdit();
                                        }
                                    }}
                                    className="inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-mld-muted-text hover:bg-mld-warning/5 hover:text-mld-warning"
                                    title="Edit panel pricing">
                                    <Pencil className="h-3 w-3" />
                                    <span>Edit</span>
                                </span>
                                {onDelete && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            onDelete();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onDelete();
                                            }
                                        }}
                                        className={clsx(
                                            'inline-flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-[10px] font-medium',
                                            isDeleting ? 'text-mld-muted-text' : 'text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger'
                                        )}
                                        title="Delete panel">
                                        <Trash2 className="h-3 w-3" />
                                    </span>
                                )}
                            </div>
                        ) : isAdmin ? (
                            <span
                                aria-hidden="true"
                                className="invisible ml-1 inline-flex items-center gap-1 px-2 py-1 text-[10px]">
                                <Pencil className="h-3 w-3" />
                                <span>Edit</span>
                            </span>
                        ) : null}
                        {!isAdmin && (
                            <span className="ml-auto">
                                {open ? (
                                    <ChevronDown className="h-3.5 w-3.5 text-mld-muted-text" />
                                ) : (
                                    <ChevronRight className="h-3.5 w-3.5 text-mld-muted-text" />
                                )}
                            </span>
                        )}
                    </Disclosure.Button>
                    <Disclosure.Panel className="pl-2">
                        <IncludedTests tests={panel.tests} isBundled={isBundled} testPrices={testPrices} calculatedTestIds={calculatedTestIds} />
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
};

/* ------------------------------------------------------------------ */
/*  Compute panel price from its assay_codes                           */
/* ------------------------------------------------------------------ */
function computePanelPrice(
    assayCodes: string[],
    assayByCode: Map<string, AssayCatalogItem>,
    multipliers: Multipliers,
    markup: number
): number {
    let total = 0;
    for (const code of assayCodes) {
        const assay = assayByCode.get(code);
        if (!assay) continue;
        const {price} = computeTestPrice(assay, multipliers, markup);
        total += price;
    }
    return Math.round(total * 100) / 100;
}

function computePanelAddonPrice(
    assayCodes: string[],
    assayByCode: Map<string, AssayCatalogItem>,
    tier: PartnerTier
): number {
    let total = 0;
    for (const code of assayCodes) {
        const assay = assayByCode.get(code);
        if (!assay) continue;
        total += getAddonPrice(assay, tier);
    }
    return Math.round(total * 100) / 100;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
interface OrganizationCatalogProps {
    orgId?: number | null;
}

export const OrganizationCatalog: React.FC<OrganizationCatalogProps> = ({orgId: orgIdOverride = null}) => {
    const {organization} = useTenant();
    const orgId = orgIdOverride ?? organization?.id ?? null;
    const hasOrg = Boolean(orgId);
    const isAdmin = orgIdOverride != null;

    const queryClient = useQueryClient();

    /* ---- Catalog data ---- */
    const catalogQuery = useQuery({
        queryKey: ['admin-org-catalog', orgId],
        queryFn: () => AdminService.getOrganizationCatalog(orgId as number),
        enabled: Boolean(orgId),
    });

    const panels = catalogQuery.data?.panels ?? [];
    const profiles = catalogQuery.data?.profiles ?? [];

    /* ---- Storefront product mapping (which profiles are published) ---- */
    const storefrontQueryKey = ['admin', 'storefront-products', orgId];
    const {data: storefrontProducts = []} = useQuery({
        queryKey: storefrontQueryKey,
        queryFn: () => AdminService.listStorefrontProducts(orgId as number),
        enabled: Boolean(orgId),
        staleTime: 30_000,
    });
    const storefrontByProfileId = new Map(
        storefrontProducts.filter((p) => p.profile_id).map((p) => [p.profile_id!, p])
    );

    const publishMutation = useMutation({
        mutationFn: async (profileId: number) => {
            const existing = storefrontByProfileId.get(profileId);
            if (existing) {
                await AdminService.deleteStorefrontProduct(orgId!, existing.id);
                return;
            }
            const profile = profiles.find((p) => p.id === profileId);
            await AdminService.createStorefrontProduct(orgId!, {
                name: profile?.name ?? 'Unnamed Product',
                price_cents: 0,
                profile_id: profileId,
                is_active: false,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: storefrontQueryKey});
        },
    });

    /* ---- Pricing data (admin-only) ---- */
    const {data: assays = []} = useQuery({
        queryKey: ['admin', 'assay-catalog'],
        queryFn: () => AdminService.getAssayCatalog(),
        enabled: isAdmin,
        staleTime: 5 * 60 * 1000,
    });

    const {data: pricingSettingsData} = useQuery({
        queryKey: ['admin', 'pricing-settings', orgId],
        queryFn: () => AdminService.getPricingSettings(orgId!),
        enabled: isAdmin && Boolean(orgId),
        staleTime: 5 * 60 * 1000,
    });

    const {data: pricingPanels = []} = useQuery({
        queryKey: ['admin', 'pricing-panels', orgId],
        queryFn: () => AdminService.listPricingPanels(orgId!),
        enabled: isAdmin && Boolean(orgId),
    });

    /* ---- Pricing state (initialized from DB) ---- */
    const [selectedTier, setSelectedTier] = useState<PartnerTier>('standard');
    const [multipliers, setMultipliers] = useState<Multipliers>({reagent: 1, calibrator: 1, shared: 1});
    const [markupOverride, setMarkupOverride] = useState<number | null>(null);
    // Editable from this screen; persisted via the pricing-settings autosave alongside
    // the other multiplier/markup fields.
    const [memberDiscountPct, setMemberDiscountPct] = useState<number | null>(null);
    const settingsInitialized = useRef(false);

    // Initialize state from DB when pricing settings load
    useEffect(() => {
        if (!pricingSettingsData || settingsInitialized.current) return;
        settingsInitialized.current = true;
        setSelectedTier(pricingSettingsData.partner_tier ?? 'standard');
        setMultipliers({
            reagent: pricingSettingsData.reagent_multiplier ?? 1,
            calibrator: pricingSettingsData.calibrator_multiplier ?? 1,
            shared: pricingSettingsData.shared_multiplier ?? 1,
        });
        setMarkupOverride(pricingSettingsData.markup_override);
        setMemberDiscountPct(pricingSettingsData.mylabs_plus_member_discount_pct);
    }, [pricingSettingsData]);

    const activeMarkup = markupOverride ?? TIER_DEFAULT_MARKUP[selectedTier];

    /* ---- Edit mode state ---- */
    const [editingPanelId, setEditingPanelId] = useState<number | null>(null);
    const [editingCodes, setEditingCodes] = useState<Set<string>>(new Set());
    const [editingPanelName, setEditingPanelName] = useState('');
    const [editingOrphanTests, setEditingOrphanTests] = useState<Array<{code: string; name: string}>>([]);

    /* ---- New panel/profile creation state ---- */
    const [creatingNewPanel, setCreatingNewPanel] = useState(false);
    const [creatingNewProfile, setCreatingNewProfile] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [newProfilePanelIds, setNewProfilePanelIds] = useState<Set<number>>(new Set());

    /* ---- Profile panel editing state ---- */
    const [editingProfilePanelsId, setEditingProfilePanelsId] = useState<number | null>(null);
    const [editingProfilePanelIds, setEditingProfilePanelIds] = useState<Set<number>>(new Set());
    const [editingProfileTestsId, setEditingProfileTestsId] = useState<number | null>(null);
    const [editingProfileTestPanelIds, setEditingProfileTestPanelIds] = useState<Set<number>>(new Set());

    /* ---- New profile test panel state ---- */
    const [newProfileTestPanelIds, setNewProfileTestPanelIds] = useState<Set<number>>(new Set());

    /* ---- Profile expand state ---- */
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

    /* ---- Auto-save pricing settings (debounced) ---- */
    const savePricingSettings = useMutation({
        mutationFn: (settings: PricingSettings) => AdminService.updatePricingSettings(orgId!, settings),
    });

    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!isAdmin || !orgId || !settingsInitialized.current) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            savePricingSettings.mutate({
                partner_tier: selectedTier,
                reagent_multiplier: multipliers.reagent,
                calibrator_multiplier: multipliers.calibrator,
                shared_multiplier: multipliers.shared,
                markup_override: markupOverride,
                mylabs_plus_member_discount_pct: memberDiscountPct,
            });
        }, 500);

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTier, multipliers, markupOverride, memberDiscountPct]);

    const createPanelMutation = useMutation({
        mutationFn: ({name, testCodes}: {name: string; testCodes: string[]}) =>
            AdminService.createPricingPanel(orgId!, name, testCodes),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success(`Panel "${variables.name}" created`);
            setEditingPanelId(null);
            setCreatingNewPanel(false);
            setEditingCodes(new Set());
            setEditingPanelName('');
        },
        onError: () => {
            toast.error('Failed to create panel');
        },
    });

    const updatePanelMutation = useMutation({
        mutationFn: ({panelId, name, testCodes}: {panelId: number; name: string; testCodes: string[]}) =>
            AdminService.updatePricingPanel(orgId!, panelId, name, testCodes),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success(`Panel "${variables.name}" updated`);
            setEditingPanelId(null);
        },
        onError: () => {
            toast.error('Failed to update panel');
        },
    });

    const createProfileMutation = useMutation({
        mutationFn: ({name, panelIds}: {name: string; panelIds: number[]}) =>
            AdminService.createProfile(orgId!, name, panelIds),
        onSuccess: (_result, variables) => {
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success(`Profile "${variables.name}" created`);
            setCreatingNewProfile(false);
            setNewProfileName('');
            setNewProfilePanelIds(new Set());
            setNewProfileTestPanelIds(new Set());
        },
        onError: () => {
            toast.error('Failed to create profile');
        },
    });

    const updateProfilePanelsMutation = useMutation({
        mutationFn: ({profileId, panelIds}: {profileId: number; panelIds: number[]}) =>
            AdminService.updateProfilePanels(profileId, panelIds),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success('Profile updated');
            setEditingProfilePanelsId(null);
            setEditingProfilePanelIds(new Set());
            setEditingProfileTestsId(null);
            setEditingProfileTestPanelIds(new Set());
        },
        onError: () => {
            toast.error('Failed to update profile');
        },
    });

    const deletePanelMutation = useMutation({
        mutationFn: ({panelId}: {panelId: number}) =>
            AdminService.deletePricingPanel(orgId!, panelId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success('Panel deleted');
            setConfirmDeletePanel(null);
        },
        onError: (err: any) => {
            const message = err?.response?.data?.error ?? err?.data?.error ?? 'Failed to delete panel';
            toast.error(message);
            setConfirmDeletePanel(null);
        },
    });

    const deleteProfileMutation = useMutation({
        mutationFn: ({profileId}: {profileId: number}) =>
            AdminService.deleteProfile(orgId!, profileId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
            toast.success('Profile deleted');
            setConfirmDeleteProfile(null);
            setSelectedProfileId(null);
        },
        onError: (err: any) => {
            const message = err?.response?.data?.error ?? err?.data?.error ?? 'Failed to delete profile';
            toast.error(message);
            setConfirmDeleteProfile(null);
        },
    });

    const [confirmDeletePanel, setConfirmDeletePanel] = useState<{id: number; name: string} | null>(null);
    const [confirmDeleteProfile, setConfirmDeleteProfile] = useState<{id: number; name: string} | null>(null);

    const isSaving = createPanelMutation.isPending || updatePanelMutation.isPending || createProfileMutation.isPending || updateProfilePanelsMutation.isPending;

    /* ---- Derived data ---- */
    const assayByCode = useMemo(() => {
        const map = new Map<string, AssayCatalogItem>();
        for (const a of assays) map.set(a.code, a);
        return map;
    }, [assays]);

    const pricingByPanelId = useMemo(() => {
        const map = new Map<number, PricingPanel>();
        for (const pp of pricingPanels) map.set(pp.id, pp);
        return map;
    }, [pricingPanels]);

    const panelById = useMemo(() => {
        const map = new Map<number, OrganizationCatalogPanel>();
        for (const p of panels) map.set(p.id, p);
        return map;
    }, [panels]);

    // Wrapper panel = panel with exactly 1 test in panel_tests.
    // Maps assay code → {panelId, testName} for non-bundled individual tests.
    const wrapperPanelByAssayCode = useMemo(() => {
        const map = new Map<string, {panelId: number; testName: string | null}>();
        for (const panel of panels) {
            if (panel.test_count !== 1) continue;
            // Find the single test that matches an assay catalog entry via elis_test_code
            const test = panel.tests.find((t) => t.in_assay_catalog);
            if (!test) continue;
            for (const assay of assays) {
                if (
                    (test.code && (assay.elis_test_code === test.code || assay.code === test.code)) ||
                    (test.name && assay.name.toLowerCase() === test.name.toLowerCase())
                ) {
                    map.set(assay.code, {panelId: panel.id, testName: test.name});
                    break;
                }
            }
        }
        return map;
    }, [panels, assays]);

    // Set of all wrapper panel IDs for quick lookup.
    // Any panel with exactly 1 test in panel_tests is a wrapper, regardless of
    // whether its test matched an assay catalog entry.
    const wrapperPanelIdSet = useMemo(() => {
        const set = new Set([...wrapperPanelByAssayCode.values()].map((v) => v.panelId));
        for (const panel of panels) {
            if (panel.test_count === 1) set.add(panel.id);
        }
        return set;
    }, [wrapperPanelByAssayCode, panels]);

    // Set of wrapper panel IDs that matched an assay catalog entry (for filtering unmatched ones in edit mode)
    const matchedWrapperPanelIds = useMemo(
        () => new Set([...wrapperPanelByAssayCode.values()].map((v) => v.panelId)),
        [wrapperPanelByAssayCode]
    );

    // Measure the longest visible (non-wrapper) panel name to set a consistent column width.
    const visiblePanels = useMemo(() => panels.filter((p) => !wrapperPanelIdSet.has(p.id)), [panels, wrapperPanelIdSet]);
    const longestPanelName = useMemo(() => {
        let longest = '';
        for (const p of visiblePanels) {
            const name = p.name || 'Unnamed Panel';
            if (name.length > longest.length) longest = name;
        }
        return longest;
    }, [visiblePanels]);
    const anyPanelHasMismatch = useMemo(
        () => visiblePanels.some((p) => p.tests.some((t) => !t.in_assay_catalog)),
        [visiblePanels]
    );

    const nameColMeasureRef = useRef<HTMLSpanElement>(null);
    const [nameColWidth, setNameColWidth] = useState<number | undefined>(undefined);
    useEffect(() => {
        if (nameColMeasureRef.current) {
            // Add padding for the alert icon if any panel has mismatched tests
            const iconSpace = anyPanelHasMismatch ? 22 : 0;
            setNameColWidth(nameColMeasureRef.current.offsetWidth + iconSpace + 8);
        }
    }, [longestPanelName, anyPanelHasMismatch]);

    const profileTestCount = useMemo(() => {
        const map = new Map<number, number>();
        for (const profile of profiles) {
            const seen = new Set<number>();
            for (const pp of profile.panels) {
                const full = panelById.get(pp.id);
                if (full) {
                    for (const t of full.tests) seen.add(t.id);
                }
            }
            map.set(profile.id, seen.size);
        }
        return map;
    }, [profiles, panelById]);

    const highlightedPanelIds = useMemo(() => {
        if (!selectedProfileId) return new Set<number>();
        const profile = profiles.find((p) => p.id === selectedProfileId);
        if (!profile) return new Set<number>();
        return new Set(profile.panels.map((p) => p.id));
    }, [selectedProfileId, profiles]);

    // Compute panel prices (admin-only)
    // Enterprise tier: use computed panel price (cost × markup). All others: use add-on price.
    const useComputedPrice = selectedTier === 'enterprise';
    const panelPrices = useMemo(() => {
        if (!isAdmin || assays.length === 0) return new Map<number, number>();
        const map = new Map<number, number>();
        for (const pp of pricingPanels) {
            const price = useComputedPrice
                ? computePanelPrice(pp.assay_codes, assayByCode, multipliers, activeMarkup)
                : computePanelAddonPrice(pp.assay_codes, assayByCode, selectedTier);
            map.set(pp.id, price);
        }
        return map;
    }, [isAdmin, assays, pricingPanels, assayByCode, multipliers, activeMarkup, useComputedPrice, selectedTier]);

    // Compute profile prices (sum of panel prices)
    const profilePrices = useMemo(() => {
        if (!isAdmin || panelPrices.size === 0) return new Map<number, number>();
        const map = new Map<number, number>();
        for (const profile of profiles) {
            let total = 0;
            for (const pp of profile.panels) {
                total += panelPrices.get(pp.id) ?? 0;
            }
            map.set(profile.id, Math.round(total * 100) / 100);
        }
        return map;
    }, [isAdmin, profiles, panelPrices]);

    // Edit mode summary via hook
    const editSummary = usePricingCalculator(assays, editingCodes, selectedTier, multipliers, markupOverride);

    /* ---- Handlers ---- */
    const handleProfileClick = (profileId: number) => {
        setSelectedProfileId((prev) => (prev === profileId ? null : profileId));
    };

    const handleTierChange = useCallback((tier: PartnerTier) => {
        setSelectedTier(tier);
        setMarkupOverride(null);
    }, []);

    const handleMultiplierChange = useCallback((field: keyof Multipliers, value: string) => {
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n < 0) return;
        setMultipliers((prev) => ({...prev, [field]: n}));
    }, []);

    const handleMarkupChange = useCallback((value: string) => {
        const n = parseFloat(value);
        if (!Number.isFinite(n) || n < 2) return;
        setMarkupOverride(n);
    }, []);

    const handleEditPanel = useCallback(
        (panelId: number) => {
            const pricing = pricingByPanelId.get(panelId);
            const catalog = panelById.get(panelId);

            // Only include assay codes that are visible in the checkbox list
            // (non-bundled, with classification set). This prevents invisible tests
            // like CBC components (HCT, HGB, RBC) from silently persisting.
            const visibleCodes = new Set(
                assays.filter((a) => !a.is_bundled_panel && a.classification != null).map((a) => a.code)
            );
            const assayCodes = new Set(
                (pricing?.assay_codes ?? []).filter((code) => visibleCodes.has(code))
            );

            // Find tests in the panel that have no assay_catalog match
            const orphans: Array<{code: string; name: string}> = [];
            if (catalog) {
                for (const t of catalog.tests) {
                    if (!t.in_assay_catalog && t.code) {
                        orphans.push({code: t.code, name: t.name ?? t.code});
                        assayCodes.add(t.code); // include so they start checked
                    }
                }
            }

            setEditingPanelId(panelId);
            setEditingCodes(assayCodes);
            setEditingOrphanTests(orphans);
            setEditingPanelName(catalog?.name ?? pricing?.name ?? '');
        },
        [pricingByPanelId, panelById, assays]
    );

    const handleCancelEdit = useCallback(() => {
        setEditingPanelId(null);
        setEditingCodes(new Set());
        setEditingPanelName('');
        setEditingOrphanTests([]);
    }, []);

    // Recursively collect all prerequisite codes for a calculated value
    const collectPrerequisites = useCallback(
        (code: string, visited = new Set<string>()): string[] => {
            if (visited.has(code)) return [];
            visited.add(code);
            const assay = assayByCode.get(code);
            if (!assay?.is_calculated || !assay.calc_prerequisites?.length) return [];
            const result: string[] = [];
            for (const prereq of assay.calc_prerequisites) {
                result.push(prereq);
                result.push(...collectPrerequisites(prereq, visited));
            }
            return result;
        },
        [assayByCode]
    );

    // Reconcile calculated values: add any whose prereqs are all met, remove any whose prereqs aren't
    const reconcileCalcValues = useCallback(
        (codes: Set<string>) => {
            const calcAssays = assays.filter((a) => a.is_calculated && a.calc_prerequisites?.length && a.elis_test_code);
            let changed = true;
            while (changed) {
                changed = false;
                for (const assay of calcAssays) {
                    const satisfied = assay.calc_prerequisites.every((p) => codes.has(p));
                    if (satisfied && !codes.has(assay.code)) {
                        codes.add(assay.code);
                        changed = true; // adding may satisfy another calc value (chaining)
                    } else if (!satisfied && codes.has(assay.code)) {
                        codes.delete(assay.code);
                        changed = true; // removing may break other calc values that depend on it
                    }
                }
            }
        },
        [assays]
    );

    const handleToggleCode = useCallback(
        (code: string) => {
            setEditingCodes((prev) => {
                const next = new Set(prev);
                if (next.has(code)) {
                    next.delete(code);
                } else {
                    next.add(code);
                    // Auto-add prerequisites for calculated values
                    const prereqs = collectPrerequisites(code);
                    for (const p of prereqs) {
                        next.add(p);
                    }
                }
                // Reconcile: auto-add satisfied calc values, remove unsatisfied ones
                reconcileCalcValues(next);
                return next;
            });
        },
        [collectPrerequisites, reconcileCalcValues]
    );

    const handleSelectAll = useCallback(() => {
        setEditingCodes(new Set(assays.map((a) => a.code)));
    }, [assays]);

    const handleClearAll = useCallback(() => {
        setEditingCodes(new Set());
    }, []);

    const handleSavePanel = useCallback(() => {
        if (!editingPanelId) return;
        const trimmed = editingPanelName.trim();
        if (!trimmed || editingCodes.size === 0) return;
        updatePanelMutation.mutate({panelId: editingPanelId, name: trimmed, testCodes: [...editingCodes]});
    }, [editingPanelId, editingPanelName, editingCodes, updatePanelMutation]);

    const handleSaveAsNew = useCallback(() => {
        const trimmed = editingPanelName.trim();
        if (!trimmed || editingCodes.size === 0) return;
        createPanelMutation.mutate({name: trimmed, testCodes: [...editingCodes]});
    }, [editingPanelName, editingCodes, createPanelMutation]);

    /* ---- New panel creation handlers ---- */
    const handleStartNewPanel = useCallback(() => {
        setCreatingNewPanel(true);
        setEditingPanelId(null);
        setEditingCodes(new Set());
        setEditingPanelName('');
    }, []);

    const handleCancelNewPanel = useCallback(() => {
        setCreatingNewPanel(false);
        setEditingCodes(new Set());
        setEditingPanelName('');
    }, []);

    /* ---- New profile creation handlers ---- */
    const handleStartNewProfile = useCallback(() => {
        setCreatingNewProfile(true);
        setNewProfileName('');
        setNewProfilePanelIds(new Set());
        setNewProfileTestPanelIds(new Set());
    }, []);

    const handleCancelNewProfile = useCallback(() => {
        setCreatingNewProfile(false);
        setNewProfileName('');
        setNewProfilePanelIds(new Set());
        setNewProfileTestPanelIds(new Set());
    }, []);

    const handleToggleNewProfilePanel = useCallback((panelId: number) => {
        setNewProfilePanelIds((prev) => {
            const next = new Set(prev);
            if (next.has(panelId)) {
                next.delete(panelId);
            } else {
                next.add(panelId);
            }
            return next;
        });
    }, []);

    const handleToggleNewProfileTestPanel = useCallback((panelId: number) => {
        setNewProfileTestPanelIds((prev) => {
            const next = new Set(prev);
            if (next.has(panelId)) {
                next.delete(panelId);
            } else {
                next.add(panelId);
            }
            return next;
        });
    }, []);

    const handleSaveNewProfile = useCallback(() => {
        const trimmed = newProfileName.trim();
        const mergedIds = [...newProfilePanelIds, ...newProfileTestPanelIds];
        if (!trimmed || mergedIds.length === 0) return;
        createProfileMutation.mutate({name: trimmed, panelIds: mergedIds});
    }, [newProfileName, newProfilePanelIds, newProfileTestPanelIds, createProfileMutation]);

    /* ---- Profile panel editing handlers ---- */
    const handleStartEditProfilePanels = useCallback(
        (profile: OrganizationCatalogProfile) => {
            setEditingProfilePanelsId(profile.id);
            setEditingProfilePanelIds(new Set(profile.panels.filter((p) => !wrapperPanelIdSet.has(p.id)).map((p) => p.id)));
        },
        [wrapperPanelIdSet]
    );

    const handleCancelEditProfilePanels = useCallback(() => {
        setEditingProfilePanelsId(null);
        setEditingProfilePanelIds(new Set());
    }, []);

    /* ---- Profile individual test editing handlers ---- */
    const handleStartEditProfileTests = useCallback(
        (profile: OrganizationCatalogProfile) => {
            setEditingProfileTestsId(profile.id);
            setEditingProfileTestPanelIds(new Set(profile.panels.filter((p) => wrapperPanelIdSet.has(p.id)).map((p) => p.id)));
        },
        [wrapperPanelIdSet]
    );

    const handleCancelEditProfileTests = useCallback(() => {
        setEditingProfileTestsId(null);
        setEditingProfileTestPanelIds(new Set());
    }, []);

    const handleToggleProfilePanel = useCallback((panelId: number) => {
        setEditingProfilePanelIds((prev) => {
            const next = new Set(prev);
            if (next.has(panelId)) {
                next.delete(panelId);
            } else {
                next.add(panelId);
            }
            return next;
        });
    }, []);

    const handleToggleProfileTestPanel = useCallback((panelId: number) => {
        setEditingProfileTestPanelIds((prev) => {
            const next = new Set(prev);
            if (next.has(panelId)) {
                next.delete(panelId);
            } else {
                next.add(panelId);
            }
            return next;
        });
    }, []);

    const handleSaveEditProfilePanels = useCallback(() => {
        if (!editingProfilePanelsId) return;
        // Keep the profile's existing test (wrapper) panel IDs, replace only regular panels
        const profile = profiles.find((p) => p.id === editingProfilePanelsId);
        const existingTestPanelIds = profile ? profile.panels.filter((p) => wrapperPanelIdSet.has(p.id)).map((p) => p.id) : [];
        const mergedIds = [...editingProfilePanelIds, ...existingTestPanelIds];
        updateProfilePanelsMutation.mutate({profileId: editingProfilePanelsId, panelIds: mergedIds});
    }, [editingProfilePanelsId, editingProfilePanelIds, profiles, wrapperPanelIdSet, updateProfilePanelsMutation]);

    const handleSaveEditProfileTests = useCallback(() => {
        if (!editingProfileTestsId) return;
        // Keep the profile's existing regular panel IDs, replace only test (wrapper) panels
        const profile = profiles.find((p) => p.id === editingProfileTestsId);
        const existingPanelIds = profile ? profile.panels.filter((p) => !wrapperPanelIdSet.has(p.id)).map((p) => p.id) : [];
        const mergedIds = [...existingPanelIds, ...editingProfileTestPanelIds];
        updateProfilePanelsMutation.mutate({profileId: editingProfileTestsId, panelIds: mergedIds});
    }, [editingProfileTestsId, editingProfileTestPanelIds, profiles, wrapperPanelIdSet, updateProfilePanelsMutation]);

    /* ---- Derived: bundled panel IDs ---- */
    const bundledPanelIds = useMemo(() => {
        const set = new Set<number>();
        for (const pp of pricingPanels) {
            if (pp.is_bundled_panel) set.add(pp.id);
        }
        return set;
    }, [pricingPanels]);

    /* ---- Render helpers ---- */
    const renderPanelRow = (panel: OrganizationCatalogPanel, badge?: string) => {
        const isEditing = editingPanelId === panel.id;
        const price = isAdmin ? (panelPrices.get(panel.id) ?? null) : null;
        const isBundled = bundledPanelIds.has(panel.id);

        // ── Per-test prices for non-bundled panels ────────────────────
        // Match assay catalog codes to panel tests by exact code or exact name.
        // Bundled panels show dashes instead of individual test prices.
        let testPrices: Map<number, number> | undefined;
        let calculatedTestIds: Set<number> | undefined;
        if (isAdmin && !isBundled && assayByCode.size > 0) {
            const pricing = pricingByPanelId.get(panel.id);
            if (pricing) {
                testPrices = new Map();
                calculatedTestIds = new Set();
                // Build lookups by code and by name for this panel's tests
                const testByCode = new Map<string, {id: number}>();
                const testByName = new Map<string, {id: number}>();
                for (const t of panel.tests) {
                    if (t.code) testByCode.set(t.code, t);
                    if (t.name) testByName.set(t.name.toLowerCase(), t);
                }
                for (const assayCode of pricing.assay_codes) {
                    const assay = assayByCode.get(assayCode);
                    if (!assay) continue;
                    // Match via elis_test_code, assay code, or name fallback
                    const match = (assay.elis_test_code ? testByCode.get(assay.elis_test_code) : undefined)
                        ?? testByCode.get(assay.code)
                        ?? testByName.get(assay.name.toLowerCase());
                    if (!match) continue;
                    if (assay.is_calculated) {
                        testPrices.set(match.id, 0);
                        calculatedTestIds.add(match.id);
                    } else if (assay.classification === 4) {
                        testPrices.set(match.id, 0);
                    } else {
                        const tp = useComputedPrice
                            ? computeTestPrice(assay, multipliers, activeMarkup).price
                            : getAddonPrice(assay, selectedTier);
                        testPrices.set(match.id, Math.round(tp * 100) / 100);
                    }
                }
            }
        }

        return (
            <PanelRow
                key={panel.id}
                panel={panel}
                badge={badge}
                price={price}
                isAdmin={isAdmin}
                isEditing={isEditing}
                isBundled={isBundled}
                testPrices={testPrices}
                calculatedTestIds={calculatedTestIds}
                nameColWidth={nameColWidth}
                onEdit={() => handleEditPanel(panel.id)}
                onCancel={handleCancelEdit}
                onDelete={isAdmin && !isBundled ? () => setConfirmDeletePanel({id: panel.id, name: panel.name || 'Unnamed Panel'}) : undefined}
                isDeleting={deletePanelMutation.isPending && confirmDeletePanel?.id === panel.id}
                editModeContent={
                    isEditing ? (
                        <PanelEditMode
                            assays={assays}
                            orphanTests={editingOrphanTests}
                            editingCodes={editingCodes}
                            onToggleCode={handleToggleCode}
                            onSelectAll={handleSelectAll}
                            onClearAll={handleClearAll}
                            multipliers={multipliers}
                            activeMarkup={activeMarkup}
                            selectedTier={selectedTier}
                            panelName={editingPanelName}
                            onPanelNameChange={setEditingPanelName}
                            onSave={handleSavePanel}
                            onSaveAsNew={handleSaveAsNew}
                            onCancel={handleCancelEdit}
                            isSaving={isSaving}
                            isExistingPanel={true}
                            summary={editSummary}
                        />
                    ) : undefined
                }
            />
        );
    };

    return (
        <div className="space-y-6">
            {/* ==================== PRICING CONTROLS (always visible for admin) ==================== */}
            {isAdmin && (
                <PricingControlsCard
                    selectedTier={selectedTier}
                    onTierChange={handleTierChange}
                    multipliers={multipliers}
                    onMultiplierChange={handleMultiplierChange}
                    activeMarkup={activeMarkup}
                    markupOverride={markupOverride}
                    onMarkupChange={handleMarkupChange}
                    onMarkupReset={() => setMarkupOverride(null)}
                    memberDiscountPct={memberDiscountPct}
                    onMemberDiscountChange={(v) => {
                        if (v === '') {
                            setMemberDiscountPct(null);
                            return;
                        }
                        const n = Number(v);
                        if (Number.isFinite(n)) setMemberDiscountPct(n);
                    }}
                    onRefresh={() => {
                        queryClient.invalidateQueries({queryKey: ['admin-org-catalog', orgId]});
                        queryClient.invalidateQueries({queryKey: ['admin', 'pricing-panels', orgId]});
                        queryClient.invalidateQueries({queryKey: ['admin', 'pricing-settings', orgId]});
                        queryClient.invalidateQueries({queryKey: ['admin', 'assay-catalog']});
                    }}
                />
            )}

            {/* ==================== PROFILES ==================== */}
            <Card>
                <Card.Header>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Card.Title>Profiles</Card.Title>
                            <span className="inline-flex items-center rounded-full bg-mld-primary/10 px-2 py-0.5 text-[10px] font-semibold text-mld-primary">
                                {profiles.length}
                            </span>
                        </div>
                        {isAdmin && !creatingNewProfile && (
                            <button
                                onClick={handleStartNewProfile}
                                className="inline-flex items-center gap-1 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark">
                                <Plus className="h-3.5 w-3.5" />
                                Add Profile
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-mld-muted-text">
                        Profiles available to this organization, with their linked panels.
                    </p>
                </Card.Header>
                <Card.Content>
                    {/* ---- Create New Profile form ---- */}
                    {creatingNewProfile && (
                        <div className="mb-4 rounded-md border border-mld-warning/30 bg-mld-warning/5/30 p-4">
                            <div className="mb-3 flex items-center gap-3">
                                <label className="text-xs font-medium text-mld-text-secondary">Profile Name:</label>
                                <input
                                    type="text"
                                    value={newProfileName}
                                    onChange={(e) => setNewProfileName(e.target.value)}
                                    placeholder="e.g. Women's Health Profile"
                                    className="flex-1 rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-sm text-mld-text-primary placeholder-gray-400 focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary"
                                />
                            </div>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">
                                Select Panels
                            </p>
                            <div className="max-h-[300px] space-y-1 overflow-y-auto">
                                {panels.filter((p) => !wrapperPanelIdSet.has(p.id)).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((panel) => (
                                    <label
                                        key={panel.id}
                                        className={clsx(
                                            'flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 transition-colors',
                                            newProfilePanelIds.has(panel.id) ? 'bg-mld-primary/5/40' : 'hover:bg-mld-menu-background'
                                        )}>
                                        <input
                                            type="checkbox"
                                            checked={newProfilePanelIds.has(panel.id)}
                                            onChange={() => handleToggleNewProfilePanel(panel.id)}
                                            className="h-3.5 w-3.5 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                        />
                                        <span className="text-xs text-mld-text-primary">{panel.name || 'Unnamed Panel'}</span>
                                        <span className="text-[10px] text-mld-muted-text">
                                            ({panel.tests.length} test{panel.tests.length === 1 ? '' : 's'})
                                        </span>
                                    </label>
                                ))}
                                {panels.filter((p) => !wrapperPanelIdSet.has(p.id)).length === 0 && (
                                    <p className="py-2 text-xs text-mld-muted-text">No panels available. Create panels first.</p>
                                )}
                            </div>
                            {/* Individual Tests for new profile */}
                            {assays.length > 0 && (
                                <>
                                    <div className="mt-3 border-t border-mld-panel-border pt-3">
                                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">
                                            Individual Tests
                                        </p>
                                    </div>
                                    <div className="max-h-[200px] space-y-1 overflow-y-auto">
                                        {assays
                                            .filter((a) => !a.is_bundled_panel && a.classification != null)
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map((assay) => {
                                                const wrapper = wrapperPanelByAssayCode.get(assay.code);
                                                const hasWrapper = !!wrapper;
                                                const isChecked = hasWrapper && newProfileTestPanelIds.has(wrapper.panelId);
                                                return (
                                                    <label
                                                        key={assay.code}
                                                        className={clsx(
                                                            'flex items-center gap-2 rounded px-3 py-1.5 transition-colors',
                                                            !hasWrapper ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                                                            isChecked ? 'bg-mld-primary/5/40' : hasWrapper ? 'hover:bg-mld-menu-background' : ''
                                                        )}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            disabled={!hasWrapper}
                                                            onChange={() => hasWrapper && handleToggleNewProfileTestPanel(wrapper.panelId)}
                                                            className="h-3.5 w-3.5 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary disabled:opacity-50"
                                                        />
                                                        <span className="text-xs text-mld-text-primary">{assay.name}</span>
                                                        <span className="text-[10px] text-mld-muted-text">({assay.code})</span>
                                                    </label>
                                                );
                                            })}
                                    </div>
                                </>
                            )}
                            <div className="mt-3 flex items-center justify-end gap-2">
                                <button
                                    onClick={handleCancelNewProfile}
                                    className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveNewProfile}
                                    disabled={!newProfileName.trim() || (newProfilePanelIds.size === 0 && newProfileTestPanelIds.size === 0) || createProfileMutation.isPending}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                                    <Save className="h-3.5 w-3.5" />
                                    {createProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
                                </button>
                            </div>
                        </div>
                    )}

                    {!hasOrg ? (
                        <p className="text-sm text-mld-muted-text">Loading organization context...</p>
                    ) : catalogQuery.isLoading ? (
                        <p className="text-sm text-mld-muted-text">Loading profiles...</p>
                    ) : catalogQuery.isError ? (
                        <p className="text-sm text-mld-danger">Failed to load profiles.</p>
                    ) : profiles.length === 0 && !creatingNewProfile ? (
                        <p className="text-sm text-mld-muted-text">No profiles found for this organization.</p>
                    ) : (
                        <div className="space-y-2">
                            {[...profiles].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((profile) => {
                                const isSelected = selectedProfileId === profile.id;
                                const isEditingPanels = editingProfilePanelsId === profile.id;
                                const regularPanels = profile.panels.filter((p) => !wrapperPanelIdSet.has(p.id));
                                const linkedTests = profile.panels.filter((p) => wrapperPanelIdSet.has(p.id));
                                const isEditingTests = editingProfileTestsId === profile.id;
                                const testCount = profileTestCount.get(profile.id) ?? 0;
                                let profilePrice: number | null = null;
                                if (isAdmin) {
                                    if (isEditingPanels || isEditingTests) {
                                        const editPanelIds = isEditingPanels
                                            ? [...editingProfilePanelIds]
                                            : profile.panels.filter((p) => !wrapperPanelIdSet.has(p.id)).map((p) => p.id);
                                        const editTestIds = isEditingTests
                                            ? [...editingProfileTestPanelIds]
                                            : profile.panels.filter((p) => wrapperPanelIdSet.has(p.id)).map((p) => p.id);
                                        profilePrice = [...editPanelIds, ...editTestIds].reduce((sum, id) => sum + (panelPrices.get(id) ?? 0), 0);
                                    } else {
                                        profilePrice = profilePrices.get(profile.id) ?? null;
                                    }
                                }
                                return (
                                    <div key={profile.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleProfileClick(profile.id)}
                                            className={clsx(
                                                'flex w-full items-center justify-between rounded-md px-4 py-2.5 text-left transition-colors',
                                                isSelected
                                                    ? 'border-y border-r border-y-mld-primary/20 border-r-mld-primary/20 border-l-[3px] border-l-mld-primary bg-mld-primary/5'
                                                    : 'border border-mld-panel-border bg-mld-background hover:bg-mld-panel-bg'
                                            )}>
                                            <div>
                                                <span
                                                    className={clsx(
                                                        'inline-flex items-center gap-1.5 text-sm font-semibold',
                                                        isSelected ? 'text-mld-primary' : 'text-mld-text-primary'
                                                    )}>
                                                    {profile.name || 'Unnamed Profile'}
                                                    {profile.panels.some((pp) => {
                                                        const full = panelById.get(pp.id);
                                                        return full?.tests.some((t) => !t.in_assay_catalog || t.is_missing_prerequisite);
                                                    }) && (
                                                        <span title="Profile contains panels with issues"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-mld-danger" /></span>
                                                    )}
                                                </span>
                                                {profile.code ? (
                                                    <span className="ml-2 text-xs text-mld-muted-text">
                                                        ({profile.code})
                                                    </span>
                                                ) : null}
                                                <div className="mt-0.5 text-[11px] text-mld-muted-text">
                                                    {formatProfileSummary(profile.panels, wrapperPanelIdSet)}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {(() => {
                                                    const sfProduct = storefrontByProfileId.get(profile.id);
                                                    return (
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                publishMutation.mutate(profile.id);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    publishMutation.mutate(profile.id);
                                                                }
                                                            }}
                                                            className={clsx(
                                                                'cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium transition',
                                                                sfProduct
                                                                    ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/15'
                                                                    : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border hover:text-mld-text-secondary'
                                                            )}
                                                            title={sfProduct ? 'Added to Storefront — click to remove' : 'Add to Storefront'}>
                                                            {sfProduct ? 'Storefront+' : 'Add to Storefront'}
                                                        </span>
                                                    );
                                                })()}
                                                {isAdmin && profilePrice != null && profilePrice > 0 && (
                                                    <span className="font-mono text-xs font-medium text-mld-primary">
                                                        ${fmt(profilePrice)}
                                                    </span>
                                                )}
                                                {isAdmin && (
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            setConfirmDeleteProfile({id: profile.id, name: profile.name || 'Unnamed Profile'});
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                setConfirmDeleteProfile({id: profile.id, name: profile.name || 'Unnamed Profile'});
                                                            }
                                                        }}
                                                        className="inline-flex cursor-pointer items-center rounded p-1 text-mld-muted-text hover:bg-mld-danger/5 hover:text-mld-danger"
                                                        title="Delete profile">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                                {isSelected ? (
                                                    <ChevronDown className="h-4 w-4 shrink-0 text-mld-primary" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4 shrink-0 text-mld-muted-text" />
                                                )}
                                            </div>
                                        </button>

                                        {isSelected && (
                                            <div className="ml-4 rounded border border-dashed border-t-0 border-mld-panel-border bg-white p-3">
                                                {/* ======== INCLUDED PANELS SECTION ======== */}
                                                <div className="mb-2 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">
                                                            Included Panels
                                                        </p>
                                                        <span className="text-[10px] text-mld-muted-text">
                                                            {isEditingPanels
                                                                ? `${editingProfilePanelIds.size} / ${panels.filter((p) => !wrapperPanelIdSet.has(p.id)).length}`
                                                                : `${profile.panels.filter((p) => !wrapperPanelIdSet.has(p.id)).length} / ${panels.filter((p) => !wrapperPanelIdSet.has(p.id)).length}`
                                                            }
                                                        </span>
                                                    </div>
                                                    {isAdmin && !isEditingPanels && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleStartEditProfilePanels(profile);
                                                            }}
                                                            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-mld-muted-text hover:bg-mld-warning/5 hover:text-mld-warning">
                                                            <Pencil className="h-3 w-3" />
                                                            Edit
                                                        </button>
                                                    )}
                                                </div>

                                                {isEditingPanels ? (
                                                    <div>
                                                        <div className="max-h-[300px] space-y-1 overflow-y-auto">
                                                            {panels.filter((p) => !wrapperPanelIdSet.has(p.id)).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((panel) => (
                                                                <label
                                                                    key={panel.id}
                                                                    className={clsx(
                                                                        'flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 transition-colors',
                                                                        editingProfilePanelIds.has(panel.id) ? 'bg-mld-primary/5/40' : 'hover:bg-mld-menu-background'
                                                                    )}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={editingProfilePanelIds.has(panel.id)}
                                                                        onChange={() => handleToggleProfilePanel(panel.id)}
                                                                        className="h-3.5 w-3.5 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                                                    />
                                                                    <span className="text-xs text-mld-text-primary">{panel.name || 'Unnamed Panel'}</span>
                                                                    <span className="text-[10px] text-mld-muted-text">
                                                                        ({panel.tests.length} test{panel.tests.length === 1 ? '' : 's'})
                                                                    </span>
                                                                    {panelPrices.get(panel.id) != null && (
                                                                        <span className="ml-auto font-mono text-xs font-medium text-mld-primary">
                                                                            ${fmt(panelPrices.get(panel.id)!)}
                                                                        </span>
                                                                    )}
                                                                </label>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={handleCancelEditProfilePanels}
                                                                className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={handleSaveEditProfilePanels}
                                                                disabled={updateProfilePanelsMutation.isPending}
                                                                className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                                                                <Save className="h-3.5 w-3.5" />
                                                                {updateProfilePanelsMutation.isPending ? 'Saving...' : 'Save Panels'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : regularPanels.length > 0 ? (
                                                    <div className="space-y-1.5">
                                                        {regularPanels.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((pp) => {
                                                            const fullPanel = panelById.get(pp.id);
                                                            const panelWithTests: OrganizationCatalogPanel = fullPanel ?? {
                                                                id: pp.id,
                                                                name: pp.name,
                                                                code: pp.code,
                                                                test_count: 0,
                                                                tests: [],
                                                            };
                                                            return renderPanelRow(panelWithTests, 'via Profile');
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p className="py-1 text-xs text-mld-muted-text">No panels linked.</p>
                                                )}

                                                {/* ======== INDIVIDUAL TESTS SECTION ======== */}
                                                <div className="mt-3 border-t border-mld-panel-border pt-3">
                                                            <div className="mb-2 flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-mld-muted-text">
                                                                        Individual Tests
                                                                    </p>
                                                                    <span className="text-[10px] text-mld-muted-text">
                                                                        {isEditingTests
                                                                            ? `${editingProfileTestPanelIds.size} / ${assays.filter((a) => !a.is_bundled_panel && a.classification != null).length}`
                                                                            : `${linkedTests.length} / ${assays.filter((a) => !a.is_bundled_panel && a.classification != null).length}`
                                                                        }
                                                                    </span>
                                                                </div>
                                                                {isAdmin && !isEditingTests && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleStartEditProfileTests(profile);
                                                                        }}
                                                                        className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-mld-muted-text hover:bg-mld-warning/5 hover:text-mld-warning">
                                                                        <Pencil className="h-3 w-3" />
                                                                        Edit
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {isEditingTests ? (
                                                                <div>
                                                                    <div className="max-h-[200px] space-y-1 overflow-y-auto">
                                                                        {/* Unmatched wrapper panels still checked — only uncheckable */}
                                                                        {[...editingProfileTestPanelIds]
                                                                            .filter((pid) => !matchedWrapperPanelIds.has(pid))
                                                                            .sort((a, b) => {
                                                                                const pa = panelById.get(a);
                                                                                const pb = panelById.get(b);
                                                                                return (pa?.tests[0]?.name ?? pa?.name ?? '').localeCompare(pb?.tests[0]?.name ?? pb?.name ?? '');
                                                                            })
                                                                            .map((pid) => {
                                                                                const full = panelById.get(pid);
                                                                                const testName = full?.tests[0]?.name ?? full?.name ?? 'Unknown';
                                                                                const testCode = full?.tests[0]?.code ?? full?.code;
                                                                                return (
                                                                                    <label
                                                                                        key={`unmatched-${pid}`}
                                                                                        className="flex cursor-pointer items-center gap-2 rounded bg-mld-danger/5 px-3 py-1.5 transition-colors">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={true}
                                                                                            onChange={() => handleToggleProfileTestPanel(pid)}
                                                                                            className="h-3.5 w-3.5 rounded border-mld-danger/30 text-mld-danger focus:ring-rose-400"
                                                                                        />
                                                                                        <span className="text-xs text-mld-text-secondary">{testName}</span>
                                                                                        {testCode && <span className="text-[10px] text-mld-muted-text">({testCode})</span>}
                                                                                        <span className="text-[10px] font-medium text-mld-danger">(no catalog match)</span>
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        {assays
                                                                            .filter((a) => !a.is_bundled_panel && a.classification != null)
                                                                            .sort((a, b) => a.name.localeCompare(b.name))
                                                                            .map((assay) => {
                                                                                const wrapper = wrapperPanelByAssayCode.get(assay.code);
                                                                                const hasWrapper = !!wrapper;
                                                                                const isChecked = hasWrapper && editingProfileTestPanelIds.has(wrapper.panelId);
                                                                                return (
                                                                                    <label
                                                                                        key={assay.code}
                                                                                        className={clsx(
                                                                                            'flex items-center gap-2 rounded px-3 py-1.5 transition-colors',
                                                                                            !hasWrapper ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                                                                                            isChecked ? 'bg-mld-primary/5/40' : hasWrapper ? 'hover:bg-mld-menu-background' : ''
                                                                                        )}>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={isChecked}
                                                                                            disabled={!hasWrapper}
                                                                                            onChange={() => hasWrapper && handleToggleProfileTestPanel(wrapper.panelId)}
                                                                                            className="h-3.5 w-3.5 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary disabled:opacity-50"
                                                                                        />
                                                                                        <span className="text-xs text-mld-text-primary">{assay.name}</span>
                                                                                        <span className="text-[10px] text-mld-muted-text">({assay.code})</span>
                                                                                        {hasWrapper && panelPrices.get(wrapper.panelId) != null && (
                                                                                            <span className="ml-auto font-mono text-xs font-medium text-mld-primary">
                                                                                                ${fmt(panelPrices.get(wrapper.panelId)!)}
                                                                                            </span>
                                                                                        )}
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                    <div className="mt-3 flex items-center justify-end gap-2">
                                                                        <button
                                                                            onClick={handleCancelEditProfileTests}
                                                                            className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                                                            Cancel
                                                                        </button>
                                                                        <button
                                                                            onClick={handleSaveEditProfileTests}
                                                                            disabled={updateProfilePanelsMutation.isPending}
                                                                            className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
                                                                            <Save className="h-3.5 w-3.5" />
                                                                            {updateProfilePanelsMutation.isPending ? 'Saving...' : 'Save Tests'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : linkedTests.length > 0 ? (
                                                                <div className="space-y-1">
                                                                    {linkedTests.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')).map((pp) => {
                                                                        const fullPanel = panelById.get(pp.id);
                                                                        const test = fullPanel?.tests[0];
                                                                        const testName = test?.name ?? pp.name;
                                                                        const testCode = test?.code ?? pp.code;
                                                                        const matched = test?.in_assay_catalog !== false;
                                                                        const price = panelPrices.get(pp.id);
                                                                        return (
                                                                            <div key={pp.id} className={clsx('flex items-center gap-2 px-3 py-1', !matched && 'opacity-40')}>
                                                                                <span className={clsx('h-1.5 w-1.5 rounded-full', matched ? 'bg-mld-primary' : 'bg-mld-danger/70')} />
                                                                                <span className="text-xs text-mld-text-secondary">{testName}</span>
                                                                                {testCode && (
                                                                                    <span className="text-[10px] text-mld-muted-text">({testCode})</span>
                                                                                )}
                                                                                {!matched && (
                                                                                    <span className="text-[10px] font-medium text-mld-danger">(no catalog match)</span>
                                                                                )}
                                                                                {isAdmin && price != null && (
                                                                                    <span className="ml-auto font-mono text-xs font-medium text-mld-primary">
                                                                                        ${fmt(price)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <p className="py-1 text-xs text-mld-muted-text">No individual tests linked.</p>
                                                            )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card.Content>
            </Card>

            {/* ==================== PANELS ==================== */}
            <Card>
                <Card.Header>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Card.Title>Panels</Card.Title>
                            <span className="inline-flex items-center rounded-full bg-mld-primary/10 px-2 py-0.5 text-[10px] font-semibold text-mld-primary">
                                {panels.length}
                            </span>
                            {highlightedPanelIds.size > 0 && (
                                <span className="text-[11px] text-mld-muted-text">
                                    · {highlightedPanelIds.size} in selected profile
                                </span>
                            )}
                        </div>
                        {isAdmin && !creatingNewPanel && (
                            <button
                                onClick={handleStartNewPanel}
                                className="inline-flex items-center gap-1 rounded-md bg-mld-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-primary-dark">
                                <Plus className="h-3.5 w-3.5" />
                                Add Panel
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-mld-muted-text">
                        Panels attached to this organization, with their linked tests.
                    </p>
                </Card.Header>
                <Card.Content>
                    {/* ---- Create New Panel form (reuses PanelEditMode) ---- */}
                    {creatingNewPanel && (
                        <div className="mb-4">
                            <PanelEditMode
                                assays={assays}
                                editingCodes={editingCodes}
                                onToggleCode={handleToggleCode}
                                onSelectAll={handleSelectAll}
                                onClearAll={handleClearAll}
                                multipliers={multipliers}
                                activeMarkup={activeMarkup}
                                selectedTier={selectedTier}
                                panelName={editingPanelName}
                                onPanelNameChange={setEditingPanelName}
                                onSave={() => {}}
                                onSaveAsNew={handleSaveAsNew}
                                onCancel={handleCancelNewPanel}
                                isSaving={isSaving}
                                isExistingPanel={false}
                                summary={editSummary}
                            />
                        </div>
                    )}

                    {!hasOrg ? (
                        <p className="text-sm text-mld-muted-text">Loading organization context...</p>
                    ) : catalogQuery.isLoading ? (
                        <p className="text-sm text-mld-muted-text">Loading panels...</p>
                    ) : catalogQuery.isError ? (
                        <p className="text-sm text-mld-danger">Failed to load panels.</p>
                    ) : panels.length === 0 && !creatingNewPanel ? (
                        <p className="text-sm text-mld-muted-text">No panels found for this organization.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {/* Hidden span to measure the longest panel name */}
                            <span
                                ref={nameColMeasureRef}
                                className="pointer-events-none invisible fixed whitespace-nowrap text-xs font-medium"
                                style={{left: -9999}}
                                aria-hidden="true">
                                {longestPanelName}
                            </span>
                            {panels
                                .filter((p) => !wrapperPanelIdSet.has(p.id))
                                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                                .map((panel) =>
                                    renderPanelRow(
                                        panel,
                                        highlightedPanelIds.has(panel.id) ? 'via Profile' : undefined
                                    )
                                )}
                        </div>
                    )}
                </Card.Content>
            </Card>

            {/* ==================== DELETE PANEL CONFIRMATION ==================== */}
            {confirmDeletePanel && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmDeletePanel(null)}>
                    <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-sm font-semibold text-mld-text-primary">Delete Panel</h3>
                        <p className="mt-2 text-sm text-mld-text-secondary">
                            Delete panel &ldquo;{confirmDeletePanel.name}&rdquo;? This will remove it from all providers in this organization.
                        </p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={() => setConfirmDeletePanel(null)}
                                className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                Cancel
                            </button>
                            <button
                                onClick={() => deletePanelMutation.mutate({panelId: confirmDeletePanel.id})}
                                disabled={deletePanelMutation.isPending}
                                className="rounded-md bg-mld-danger px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-danger/80 disabled:opacity-50">
                                {deletePanelMutation.isPending ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== DELETE PROFILE CONFIRMATION ==================== */}
            {confirmDeleteProfile && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmDeleteProfile(null)}>
                    <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-sm font-semibold text-mld-text-primary">Delete Profile</h3>
                        <p className="mt-2 text-sm text-mld-text-secondary">
                            Delete profile &ldquo;{confirmDeleteProfile.name}&rdquo;? This will remove it from all providers in this organization.
                        </p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={() => setConfirmDeleteProfile(null)}
                                className="rounded-md border border-mld-panel-border bg-white px-3 py-1.5 text-xs font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                Cancel
                            </button>
                            <button
                                onClick={() => deleteProfileMutation.mutate({profileId: confirmDeleteProfile.id})}
                                disabled={deleteProfileMutation.isPending}
                                className="rounded-md bg-mld-danger px-3 py-1.5 text-xs font-medium text-white hover:bg-mld-danger/80 disabled:opacity-50">
                                {deleteProfileMutation.isPending ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationCatalog;
